// R-NAV-2 — placeholder ("coming soon") destinations.
//
// The redesigned role sidebars (spec §3.2) link to the REAL tools where they
// exist and to these not-yet-built destinations otherwise. Per the owner's
// rule (build complete-and-correct; placeholder ONLY when content depends on a
// later sub-R): each entry below is a real, role-reachable route that renders a
// clean ComingSoon page with the CORRECT title — the navigation structure is
// final; the page content lands in its owning sub-R (R-SIS, R-ASSESS, R-PAY,
// R-AI, R-ROLES, R-SUPER, R-PARENT, …).
//
// The map is the single source of truth for BOTH the page title (ComingSoon
// looks it up by route key) AND the router registration (router.tsx maps the
// keys to ComingSoon route entries). Keys mirror the route id / two-segment
// path exactly as the sidebar links navigate to them.

/** routeKey (1- or 2-segment path) → Persian page title + owning sub-R. */
export const PLACEHOLDER_ROUTES: Readonly<Record<string, { title: string; phase: string }>> = {
  // ----- Student (R-SIS / R-PAY / R-AI) -----
  // "add-drop" — SHIPPED in R-SIS-3d (real page at /add-drop). "registration"
  // already had a real route; both now use pages/Registration.
  // "schedule" — SHIPPED (real WeeklySchedule at /schedule, from registration/me).
  // assignments / cognitive-profile / adaptive-path — SHIPPED as real interactive pages.
  // "service-desk" — SHIPPED in R-SIS-5b (inbox/new/detail at /service-desk*).
  // "tuition" — SHIPPED in R-PAY-1d (balance/pay/installments at /tuition).
  // "portfolio" — SHIPPED (real Portfolio = completed coursework at /portfolio).

  // ----- Instructor — SHIPPED as rich interactive pages (teaching tools). -----

  // ----- Faculty admin (R-SIS) -----
  // "faculty-dashboard" — SHIPPED (real FacultyAdminDashboard at /faculty-dashboard).
  // "curriculum-builder" — SHIPPED in R-SIS-2 (real page at /curriculum-builder).
  // "special-cases" — SHIPPED as the real Commission page (/admin/commission,
  // pages/admin/Commission.tsx + CommissionCase model); the stale placeholder
  // entry was dropped in S-21 (no sidebar links to it — verified by the
  // nav-coherence vitest, which fails loudly on any NEW placeholder link).

  // ----- Counselor (WELL-4) — ALL SHIPPED -----
  // "counselor-inbox" / "counseling-appointments" / "crisis-queue" — SHIPPED.
  // All three render the real CounselorInbox page; the latter two are the same
  // workspace with the active tab pre-selected from the route id (own-appointments
  // + open crisis queue + scoped student summaries). No placeholders needed.
  // instructor-assignment / probation-review / faculty-reports — SHIPPED as rich pages.
  // "service-desk-admin" — SHIPPED in R-SIS-5b (staff approval inbox = the
  // "در انتظار تأیید من" tab inside /service-desk; sidebar points there).

  // ----- System admin (R-SIS / R-RBAC / R-GOV / R-ANALYTICS) -----
  // "admin/courses" — SHIPPED as a rich master course catalog page.
  // "admin/users" — SHIPPED (§9.3 fast-follow): real CRUD + role assignment at /admin/users.
  // admin governance/ops (roles, calendar, integrations, notif-templates, broadcast,
  // ai-governance, analytics-admin) + super tools (tenants) — SHIPPED as rich pages.
  // "monitoring" — SHIPPED as the REAL system-health console (SystemMonitoring,
  // R-MON W12/W13: prisma counts + Node runtime depth).
  // R-MON W12 (honesty): "impersonate" + "multitenancy" are platform-ops MOCKS
  // (DemoBadge, no backend) — production is single-tenant, so they're no longer
  // advertised in the super_admin nav (page files remain, routes left registered).

  // parent (grades/attendance/schedule/tuition), support (tickets/user-lookup),
  // ta (grading-queue), moderator (flagged-reports/community-moderation/auto-rules/
  // moderation-log), content_manager (content-review/content-analytics), org (users/
  // bulk-enroll/seats/invoices/reports) — ALL SHIPPED as rich interactive pages.
};

/** Look up a placeholder entry by route key (1- or 2-segment). */
export const getPlaceholder = (
  routeKey: string,
): { title: string; phase: string } | undefined => PLACEHOLDER_ROUTES[routeKey];
